import {
  formatearDia,
  formatearFecha,
  formatearRevision,
  NOMBRE_SITUACION,
  relativo,
  SITUACIONES,
  type Situacion,
} from "@gestiondoc/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, notFound, useParams, useRouteContext } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTRPC } from "../api.ts";
import { Encabezado, Kpi } from "../componentes/Encabezado.tsx";
import { Lectores } from "../componentes/Lectores.tsx";
import {
  BotonEnlace,
  CABECERA,
  CLASE_ENTRADA,
  Enlace,
  EstadoVacio,
  PildoraSituacion,
  Tabla,
  TD,
  TH,
} from "../componentes/ui.tsx";
import type { FilaLista } from "../tipos.ts";
import { abreviarNombre, textoDeMeses } from "../utiles.ts";

const CHIPS: { codigo: string | undefined; nombre: string }[] = [
  { codigo: undefined, nombre: "Todos" },
  { codigo: "INS", nombre: "Instructivos" },
  { codigo: "REG", nombre: "Registros" },
  { codigo: "PRO", nombre: "Procedimientos" },
  { codigo: "EXT", nombre: "Externos" },
];

/** Pantalla 01 · Lista maestra de documentos del proceso. */
export function PaginaProceso() {
  const { codigo } = useParams({ from: "/app/procesos/$codigo" });
  const { perfil } = useRouteContext({ from: "/app" });
  const proceso = perfil.procesos.find((p) => p.codigo === codigo);
  if (!proceso) throw notFound();

  const trpc = useTRPC();
  const [busqueda, setBusqueda] = useState("");
  // Se consulta cuando se deja de escribir, no con cada tecla.
  const [buscada, setBuscada] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setBuscada(busqueda.trim()), 250);
    return () => clearTimeout(t);
  }, [busqueda]);
  const [tipo, setTipo] = useState<string | undefined>(undefined);
  const [situacion, setSituacion] = useState<Situacion | undefined>(undefined);
  const lista = useQuery({
    ...trpc.documentos.listar.queryOptions({
      procesoCodigo: codigo,
      busqueda: buscada || undefined,
      tipoCodigo: tipo,
      situacion,
    }),
    // Mientras llega el resultado nuevo, la tabla sigue mostrando el anterior.
    placeholderData: keepPreviousData,
  });

  const puedeCargar = proceso.rol === "carga";
  const esAdministrador = perfil.usuario.rolGlobal === "administrador";
  const kpis = lista.data?.kpis;

  return (
    <div>
      <Encabezado
        overline={proceso.nombre}
        titulo="Documentos del proceso"
        acciones={
          (puedeCargar || esAdministrador) && (
            <>
              {esAdministrador && (
                // Fase 5: un administrador trae de CSGestión documentos con todas sus revisiones.
                <BotonEnlace to="/historial/importar" search={{ proceso: codigo }}>
                  Importar historial
                </BotonEnlace>
              )}
              {puedeCargar && (
                <>
                  <Enlace href={`/reportes/auditoria.xlsx?proceso=${codigo}`}>
                    Exportar control de documentos
                  </Enlace>
                  <BotonEnlace to="/procesos/$codigo/nuevo" params={{ codigo }} variante="primario">
                    + Nuevo documento
                  </BotonEnlace>
                </>
              )}
            </>
          )
        }
      />

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <Kpi valor={kpis?.vigentes ?? "—"} etiqueta="Documentos vigentes" />
        <Kpi
          valor={kpis?.esperanTuFirma ?? "—"}
          etiqueta="Esperan tu firma"
          tono={kpis && kpis.esperanTuFirma > 0 ? "ambar" : "marino"}
        />
        <Kpi
          valor={kpis?.revisionVencida ?? "—"}
          etiqueta="Revisión anual vencida"
          tono={kpis && kpis.revisionVencida > 0 ? "rojo" : "marino"}
        />
        <Kpi
          valor={kpis?.retencionPorVencer ?? "—"}
          etiqueta="Registros que cumplen retención"
          tono={kpis && kpis.retencionPorVencer > 0 ? "rojo" : "marino"}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por código, nombre o contenido…"
          className={`${CLASE_ENTRADA} max-w-[320px]`}
        />
        <div className="flex flex-wrap gap-1.5">
          {CHIPS.map((c) => (
            <button
              key={c.nombre}
              type="button"
              onClick={() => setTipo(c.codigo)}
              className={`rounded-full border px-3 py-1 text-[12px] font-semibold ${
                tipo === c.codigo
                  ? "border-marino bg-marino text-white"
                  : "border-borde bg-white text-texto-2 hover:bg-fondo"
              }`}
            >
              {c.nombre}
            </button>
          ))}
        </div>
        <select
          value={situacion ?? ""}
          onChange={(e) => setSituacion((e.target.value || undefined) as Situacion | undefined)}
          className={`${CLASE_ENTRADA} ml-auto w-auto`}
        >
          <option value="">Estado: todos</option>
          {SITUACIONES.map((s) => (
            <option key={s} value={s}>
              {NOMBRE_SITUACION[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        {lista.isPending && <EstadoVacio>Cargando…</EstadoVacio>}
        {lista.isError && (
          <EstadoVacio>No se pudo cargar la lista: {lista.error.message}</EstadoVacio>
        )}
        {lista.data && lista.data.documentos.length === 0 && (
          <EstadoVacio>
            {busqueda || tipo || situacion
              ? "Ningún documento coincide con el filtro."
              : "Este proceso todavía no tiene documentos."}
            {!puedeCargar && (
              <div className="mt-2 text-[12px]">
                En este proceso tienes acceso de consulta: puedes leer y dar acuse, no cargar.
              </div>
            )}
          </EstadoVacio>
        )}
        {lista.data && lista.data.documentos.length > 0 && (
          <ListaMaestra filas={lista.data.documentos} />
        )}
      </div>
    </div>
  );
}

/** La lista maestra: una fila por documento, con su situación y la lectura del personal. */
function ListaMaestra({ filas }: { filas: FilaLista[] }) {
  return (
    <Tabla minimo={820}>
      <thead>
        <tr className={CABECERA}>
          <th className={TH}>Código</th>
          <th className={TH}>Documento</th>
          <th className={TH}>Tipo</th>
          <th className={TH}>Rev.</th>
          <th className={TH}>Estado</th>
          <th className={TH}>Próxima revisión</th>
          <th className={TH}>Lectura</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((f) => (
          <tr key={f.id} className="border-b border-borde/60 last:border-0 hover:bg-fondo">
            <td className={`${TD} font-mono text-[12.5px] font-medium text-marino`}>
              <Link to="/documentos/$codigo" params={{ codigo: f.codigo }}>
                {f.codigo}
              </Link>
            </td>
            <td className={TD}>
              <Link
                to="/documentos/$codigo"
                params={{ codigo: f.codigo }}
                className="font-semibold text-texto hover:text-marino"
              >
                {f.nombre}
              </Link>
              <div className="text-[11.5px] text-texto-3">{contextoDe(f)}</div>
              {f.fragmento && (
                <div className="mt-0.5 text-[11.5px] text-texto-2 italic">
                  <Fragmento texto={f.fragmento} />
                </div>
              )}
            </td>
            <td className={`${TD} text-texto-2`}>{f.tipo.nombre}</td>
            <td className={`${TD} font-mono text-[12.5px]`}>
              {f.revision ? formatearRevision(f.revision) : "—"}
            </td>
            <td className={TD}>
              <PildoraSituacion situacion={f.situacion} />
            </td>
            <td className={`${TD} text-texto-2`}>{proximaRevisionDe(f)}</td>
            <td className={`${TD} text-texto-3`}>
              {f.tipo.esRegistro ? "No aplica" : f.lectura ? <Lectores lectura={f.lectura} /> : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </Tabla>
  );
}

/** La segunda línea de la columna Documento, con los mismos textos del mockup. */
function contextoDe(f: FilaLista): string {
  switch (f.situacion) {
    case "borrador":
      return `Borrador · ${f.elaboradoPor ? abreviarNombre(f.elaboradoPor) : ""}`;
    case "en_revision":
    case "esperando_aprobacion":
      return `Enviado a revisión ${f.enviadaEn ? relativo(f.enviadaEn) : ""} · ${
        f.elaboradoPor ? abreviarNombre(f.elaboradoPor) : ""
      }`;
    case "retencion_por_vencer":
      return f.retencion
        ? `Retención ${textoDeMeses(f.retencion.meses)} · Cumple ${formatearDia(f.retencion.cumpleEl)}`
        : "";
    case "vigente":
    case "revision_vencida":
      return f.aprobadaEn
        ? `Aprobado ${formatearFecha(f.aprobadaEn)} · ${
            f.aprobadoPor ? abreviarNombre(f.aprobadoPor) : ""
          }`
        : "";
    case "sin_vigente":
      return "Sin revisión vigente";
    case "dispuesto":
      return "Cumplió su retención y se dispuso";
    case "retirado":
      return "Retirado del sistema de gestión · solo consulta";
  }
}

function proximaRevisionDe(f: FilaLista): string {
  if (f.situacion === "en_revision" || f.situacion === "esperando_aprobacion")
    return "Tras aprobar";
  if (f.tipo.esRegistro) return "No aplica";
  return f.proximaRevision ? formatearDia(f.proximaRevision) : "—";
}

/** El fragmento de la búsqueda por contenido: lo que va entre «» es la coincidencia. */
function Fragmento({ texto }: { texto: string }) {
  const partes = texto.split(/(«[^»]*»)/g).filter((x) => x.length > 0);
  return (
    <>
      …
      {partes.map((parte, i) =>
        parte.startsWith("«") ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: el orden es el del texto y no cambia
          <mark key={i} className="rounded bg-ambar-fondo px-0.5 text-texto not-italic">
            {parte.slice(1, -1)}
          </mark>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: ídem
          <span key={i}>{parte}</span>
        ),
      )}
      …
    </>
  );
}
